import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useWorkspace } from 'stores/workspaceStore';

import ROLE from 'assets/userRoles.json';
import { PageRoutes } from 'constant/pageRoute';
import { setSelectedUserId } from 'redux/selected-account/selected-account.slice';
import { AppDispatch, RootState } from 'redux/store';
import {
    formatAccountName,
    isSpouseAccount,
    resolveSelectedUserId,
    type SwitchableAccount,
} from 'utils/selectedAccount';

export interface UseAccountSwitcherResult {
    accounts: SwitchableAccount[];
    canSwitch: boolean;
    selected: SwitchableAccount | null;
    switchTo: (userId: number) => void;
}

const useAccountSwitcher = (): UseAccountSwitcherResult => {
    const dispatch = useDispatch<AppDispatch>();
    const user = useSelector((state: RootState) => state.user.entity);
    const selectedUserId = useSelector((state: RootState) => state.selectedAccount.selectedUserId);
    const { isSidebarLayout } = useWorkspace();

    const spouse = isSpouseAccount(user.spouse) ? user.spouse : null;
    const canSwitch = user.role === ROLE.externalClient && spouse !== null && user.id > 0;

    const accounts = useMemo<SwitchableAccount[]>(() => {
        if (!canSwitch || !spouse) {
            return [];
        }

        return [
            {
                id: user.id,
                firstname: user.firstname,
                lastname: user.lastname,
                kind: 'self',
            },
            {
                id: spouse.id,
                firstname: spouse.firstname ?? '',
                lastname: spouse.lastname ?? '',
                kind: 'spouse',
            },
        ];
    }, [canSwitch, spouse, user.firstname, user.id, user.lastname]);

    const selected = useMemo<SwitchableAccount | null>(() => {
        if (!accounts.length) {
            return null;
        }
        const resolvedId = resolveSelectedUserId(user.id, spouse, selectedUserId);
        return accounts.find((account) => account.id === resolvedId) ?? accounts[0];
    }, [accounts, selectedUserId, spouse, user.id]);

    const switchTo = useCallback(
        (userId: number) => {
            if (!canSwitch || !user.id || userId === selected?.id) {
                return;
            }

            const next = accounts.find((account) => account.id === userId);
            if (!next) {
                return;
            }

            dispatch(setSelectedUserId({ ownerUserId: user.id, selectedUserId: next.id }));

            const homePath = isSidebarLayout ? PageRoutes.dashboard : '/visa-process';
            window.location.assign(homePath);
        },
        [accounts, canSwitch, dispatch, isSidebarLayout, selected?.id, user.id]
    );

    return {
        accounts,
        canSwitch,
        selected,
        switchTo,
    };
};

export const getSwitchableAccountName = (account: SwitchableAccount): string =>
    formatAccountName(account.firstname, account.lastname);

export default useAccountSwitcher;
